import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses';
import { formatProductOfferingPrice } from '../../common/utils/priceUtilsCoCo.js';
import {
  getLocalizedString,
  getProductOfferingPriceDataWithFallback,
} from '../../common/utils/productOfferingsUtils.js';
import { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import { Price } from '../ToggleCheckbox/ToggleCheckbox.js';
import type { BundleAddonGroup } from '../../generated/api/bundleAddonGroup.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';
import type { ReactNode } from 'react';

import './BundleAddonGroupSelectorCoCo.scss';

export interface BundledAddonGroupSelection {
  groupId: string;
  selectedOfferingIds: string[];
}

interface BundleAddonGroupSelectorProps {
  group: BundleAddonGroup | CartBundleAddonGroup;
  selectedOfferingIds: string[];
  onSelectionChange: (groupId: string, selectedIds: string[]) => void;
  errorMessage?: string;
  srOnlyLabel?: boolean;
  renderOfferingContent?: (offering: NonNullable<BundleAddonGroup['productOffering']>[number]) => ReactNode;
}

export const BundleAddonGroupSelectorCoCo = ({
  group,
  selectedOfferingIds,
  onSelectionChange,
  errorMessage,
  srOnlyLabel = false,
  renderOfferingContent,
}: BundleAddonGroupSelectorProps) => {
  const offerings = group.productOffering ?? [];
  const isSingleSelect = group.cardinality?.maxNum === 1;
  const groupName = getLocalizedString(group.name);

  // RC is preferred over OC to match the other addon price displays, but the suffix follows the actual price type.
  const getOfferingPriceInfo = (productOfferingPrice: ProductOfferingPrice[] | undefined) => ({
    value: formatProductOfferingPrice(
      getProductOfferingPriceDataWithFallback(
        productOfferingPrice,
        ProductOfferingPrice.PriceTypeEnum.RC,
        ProductOfferingPrice.PriceTypeEnum.OC
      )
    ),
  });

  if (offerings.length === 0) {
    return null;
  }

  if (isSingleSelect) {
    const selectedValue = selectedOfferingIds[0] ?? '';
    const dropdownItems = offerings.map(offering => ({
      label: getLocalizedString(offering.name),
      value: offering.id,
    }));

    const selectedOffering = offerings.find(offering => offering.id === selectedValue) ?? offerings[0];
    const priceInfo = getOfferingPriceInfo(selectedOffering?.productOfferingPrice);

    return (
      <div className={`${dsClass.MARGIN_TOP_2}`}>
        <div
          className={`${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID} ${dsClass.PADDING_2} ${dsClass.GRID} bundle-addon-group`}
        >
          <div className={`${dsClass.GRID_ROW}`}>
            <div
              className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
            >
              <CL.Dropdown
                items={dropdownItems}
                selectedValue={selectedValue}
                label={groupName}
                srOnlyLabel={srOnlyLabel}
                onValueChange={(element: HTMLLIElement) => {
                  const value = element.dataset.value;
                  if (value) {
                    onSelectionChange(group.id, [value]);
                  }
                }}
              />
            </div>
            <div
              className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
            >
              <Price priceInfo={priceInfo} />
            </div>
          </div>
          {renderOfferingContent?.(selectedOffering)}
          {errorMessage && <CL.InputError>{errorMessage}</CL.InputError>}
        </div>
      </div>
    );
  }

  // Multi-select: maxNum > 1 or 0 (0 means unlimited/optional)
  const maxNum = group.cardinality?.maxNum ?? 0;
  const isMaxReached = maxNum > 0 && selectedOfferingIds.length >= maxNum;

  return (
    <div className={`${dsClass.MARGIN_TOP_2}`}>
      <div
        className={`${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID} ${dsClass.PADDING_2} ${dsClass.GRID} bundle-addon-group`}
      >
        <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2}`}>{groupName}</div>
        <CL.Checkbox.Group>
          {offerings.map(offering => {
            const isChecked = selectedOfferingIds.includes(offering.id);
            const isDisabled = !isChecked && isMaxReached;
            const priceInfo = getOfferingPriceInfo(offering?.productOfferingPrice);
            return (
              <div className={`${dsClass.GRID_ROW}`} key={offering.id}>
                <div
                  className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
                >
                  <CL.Checkbox
                    key={`${offering.id}-${isChecked ? 'checked' : 'unchecked'}`}
                    value={offering.id}
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => {
                      if (isChecked) {
                        onSelectionChange(
                          group.id,
                          selectedOfferingIds.filter(id => id !== offering.id)
                        );
                      } else if (!isMaxReached) {
                        onSelectionChange(group.id, [...selectedOfferingIds, offering.id]);
                      }
                    }}
                  >
                    {getLocalizedString(offering.name)}
                  </CL.Checkbox>
                </div>
                <div
                  className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
                >
                  <Price priceInfo={priceInfo} />
                </div>
              </div>
            );
          })}
        </CL.Checkbox.Group>
        {errorMessage && <CL.InputError>{errorMessage}</CL.InputError>}
      </div>
    </div>
  );
};
